import React, { useState } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import GoogleMapWithStreetView from './components/GoogleMapWithStreetView';

let DefaultIcon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});
L.Marker.prototype.options.icon = DefaultIcon;

function App() {
  const [slid, setSlid] = useState("");
  const [address, setAddress] = useState("");
  const [county, setCounty] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [loading, setLoading] = useState(false);
  const [cassState, setCassState] = useState(false);
  const [testIndex, setTestIndex] = useState(0);
  const [localState, setLocalState] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [reverseResult, setReverseResult] = useState(null);
  const [baseAddressParts, setBaseAddressParts] = useState({ streetNumber: '', streetName: '', city: '', state: '', zipcode: '', county: ''});
  const [showReverseResults, setShowReverseResults] = useState(false);
  const [reconstructedAddress, setReconstructedAddress] = useState("");
  const [revalidationResult, setRevalidationResult] = useState(null);
  const [batchValidationResults, setBatchValidationResults] = useState([]);
  const [geocodedCoordinates, setGeocodedCoordinates] = useState([]);
  const [selectedCoordinateIndex, setSelectedCoordinateIndex] = useState(0);
  const selectedCoordinate = geocodedCoordinates[selectedCoordinateIndex];

  const handleSmartPaste = (e) => {
    const pastedText = e.clipboardData.getData("Text");
    const [pastedSlid, pastedAddress, pastedLat, pastedLng] = pastedText.split('\t');
    if (pastedSlid && pastedAddress && pastedLat && pastedLng) {
      e.preventDefault(); 
      setSlid(pastedSlid.trim());
      setAddress(pastedAddress.trim());
      setLat(pastedLat.trim());
      setLng(pastedLng.trim());
    }
  };

  const testCases = [
    {
      slid: "229813617",
      address: "1679 LAS VIRGENES CANYON RD, CALABASAS, CA 91302",
      lat: "34.09493823",
      lng: "-118.7084628"
    },
    {
      slid: "254238226",
      address: "198 PLEASANT VALLEY RD, LANCASTER, NH 03584",
      lat: "44.49840268",
      lng: "-71.49266225"
    },
    {
      slid: "70043310",
      address: "554 NORTH RD, LANCASTER, NH 03584",
      lat: "44.48674641",
      lng: "-71.54881787"
    },
    {
      slid: "69302267",
      address: "13816 STATE HIGHWAY 478, MESILLA PARK, NM 88047",
      lat: "32.18173691",
      lng: "-106.7070398"
    }
  ];

  const runNextTestCase = () => {
    const test = testCases[testIndex];
    setSlid(test.slid);
    setAddress(test.address);
    setLat(test.lat);
    setLng(test.lng);
  
    setTimeout(() => {
      handleAddressValidation();
    }, 0);
  
    setTestIndex((prev) => (prev + 1) % testCases.length);
  };

  const handleAddressValidation = async () => {
    if (!address.trim()) {
      // alert("Please enter an address first.");
      return;
    }
    setLoading(true);
    try {
      const response = localState ? 
        await fetch("http://localhost:3001/api/google-address-validation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address, enableUspsCass: cassState })}) : 
        await fetch("/api/google-address-validation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address, enableUspsCass: cassState })});
      const data = await response.json();
      if (data.error) {
        console.error("Address validation error:", data.error);
        return;
      }
      const dpvFootnote = localState ? data.result?.uspsData?.dpvFootnote || "N/A" : data.result?.result?.uspsData?.dpvFootnote || "N/A";
      const formattedAddress = localState ? data.result?.address?.formattedAddress || "N/A" : data.result?.result?.address?.formattedAddress || "N/A";
      setCounty (localState ? data.result?.uspsData?.county ?? "" : data.result?.result?.uspsData?.county ?? "");
      // console.log(county)
      setValidationResult({
        address,
        formattedAddress,
        dpvFootnote
      });
      const { streetNumber, streetName, city, state, zipcode} = extractBaseAddressParts(address);
      setBaseAddressParts({ streetNumber, streetName, city, state, zipcode});
      if (dpvFootnote === "AABB" || lat === "" || lat === null || lat === undefined || lng === "" || lng === null || lng === undefined ) {
        setShowReverseResults(false);
        // setSearchAddress(address)
        await handleForwardGeocoding(address);
      } else if (lat && lng) {
        setShowReverseResults(true);
        await handleReverseGeocode(lat, lng);
        await handleRevalidateAllCombinations();
      } else {
        alert("Missing latitude and longitude for reverse geocoding.");
      }
    } catch (error) {
      console.error("Error during address validation:", error);
    } finally {
       setLoading(false);
    }
  };

  const extractBaseAddressParts = (fullAddress) => {
    if (!fullAddress || typeof fullAddress !== "string") {
      return { streetNumber: "", streetName: "", city: "", state: "", zipcode: ""};
    }
  
    // Normalize spacing and casing
    let cleanAddress = fullAddress.trim().replace(/\s+/g, " ");
  
    // Try splitting by commas first
    let parts = cleanAddress.includes(",") 
      ? cleanAddress.split(",").map(p => p.trim())
      : cleanAddress.split(" "); // fallback: split by spaces if no commas
  
    let streetNumber = "";
    let streetName = "";
    let city = "";
    let state = "";
    let zipcode = "";
  
    // If commas exist (more structured input)
    if (parts.length >= 3 && cleanAddress.includes(",")) {
      // Street
      const streetMatch = parts[0].match(/^(\d+)\s+(.*)$/);
      streetNumber = streetMatch ? streetMatch[1] : "";
      streetName = streetMatch ? streetMatch[2] : parts[0];
  
      // City
      city = parts[1] || "";
  
      // State + ZIP
      const stateZipMatch = parts[2].match(/^([A-Za-z]{2})\s*(\d{5}(?:-\d{4})?)$/);
      state = stateZipMatch ? stateZipMatch[1].toUpperCase() : "";
      zipcode = stateZipMatch ? stateZipMatch[2] : "";
    } 
    else {
      // No commas — try matching from the end backwards
      const stateZipRegex = /([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/;
      const stateZipMatch = cleanAddress.match(stateZipRegex);
  
      if (stateZipMatch) {
        state = stateZipMatch[1].toUpperCase();
        zipcode = stateZipMatch[2];
  
        // Remove state+ZIP from string
        cleanAddress = cleanAddress.replace(stateZipRegex, "").trim();
      }
  
      // Now get street number/name and city
      const firstSpaceIndex = cleanAddress.indexOf(" ");
      if (firstSpaceIndex > 0) {
        streetNumber = cleanAddress.slice(0, firstSpaceIndex);
        const remaining = cleanAddress.slice(firstSpaceIndex + 1).trim();
  
        // Heuristic: assume last word(s) before state are city
        const remainingParts = remaining.split(" ");
        if (remainingParts.length > 1) {
          city = remainingParts.pop();
          streetName = [remainingParts.shift(), ...remainingParts].join(" ");
        } else {
          streetName = remaining;
        }
      }
    }
  
    return { streetNumber, streetName, city, state, zipcode };
  };

  const handleReverseGeocode = async (lat, lng) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({ lat, lng });

      const endpoints = localState ?
        [{ name: "Reverse Google Geocode", url: `http://localhost:3001/api/reverse-google?${queryParams}` },
        { name: "Reverse TomTom Geocode", url: `http://localhost:3001/api/reverse-tomtom?${queryParams}` }] :
        [{ name: "Reverse Google Geocode", url: `/api/reverse-google?${queryParams}` },
        { name: "Reverse TomTom Geocode", url: `/api/reverse-tomtom?${queryParams}` }];

      const allResults = [];

      for (const endpoint of endpoints) {
        //console.log(`Calling: ${endpoint.name} - ${endpoint.url}`);
        const res = await fetch(endpoint.url);
        const json = await res.json();
        // console.log(`${endpoint.name} Response:`, json);

        const items = json.results || json.addresses || [];

        items.forEach(item => {
          let reverseStreet = "";
          // let city = "";
          // let zip = "";
          if (item.address) {
            // TomTom structure
            reverseStreet = item.address.street || item.address.streetName || "";
          }

          if (item.address_components) {
            // Google structure
            const getComp = (type) =>
              item.address_components.find((c) => c.types.includes(type))?.long_name;
              reverseStreet = reverseStreet || getComp("route");
          }
          if (reverseStreet) {
            allResults.push({ reverseStreet });
          }
        });
      }
      const dedupedStreets = deduplicateStreets(allResults.map(r => r.reverseStreet).filter(Boolean));
      setReverseResult({ dedupedStreets });
    } catch (error) {
      console.error("Error during reverse geocoding:", error);
    } finally {
      // setLoading(false);
    }
  };

  function deduplicateStreets(streets) {
    const seen = new Set();
    const result = [];
    for (const raw of streets) {
      const norm = normalizeStreetName(raw);
      if (!seen.has(norm)) {
        seen.add(norm);
        result.push(raw); // return original input
      }
    }
    return result;
  }

  function normalizeStreetName(name) {
    if (!name) return "";
    const words = name.toUpperCase().split(" ");
    const lastWord = words[words.length - 1];
    const suffix = USPS_SUFFIXES[lastWord] || lastWord;
    return [...words.slice(0, -1), suffix].join(" ");
  }

  const USPS_SUFFIXES = {
    'ALLEE': 'ALY', 'ALLEY': 'ALY', 'ALLY': 'ALY', 'ALY': 'ALY',
    'AVENUE': 'AVE', 'AV': 'AVE', 'AVEN': 'AVE', 'AVENU': 'AVE', 'AVN': 'AVE', 'AVNUE': 'AVE', 'AVE': 'AVE',
    'BAYOO': 'BYU', 'BAYOU': 'BYU', 'BYU': 'BYU',
    'BEACH': 'BCH', 'BCH': 'BCH',
    'BEND': 'BND', 'BND': 'BND',
    'BLUFF': 'BLF', 'BLUF': 'BLF', 'BLF': 'BLF',
    'BOTTOM': 'BTM', 'BOT': 'BTM', 'BTM': 'BTM',
    'BOULEVARD': 'BLVD', 'BOUL': 'BLVD', 'BOULV': 'BLVD', 'BLVD': 'BLVD',
    'BRANCH': 'BR', 'BRNCH': 'BR', 'BR': 'BR',
    'BRIDGE': 'BRG', 'BRG': 'BRG',
    'BROOK': 'BRK', 'BRK': 'BRK',
    'BURG': 'BG', 'BG': 'BG',
    'BYPASS': 'BYP', 'BYPA': 'BYP', 'BYPAS': 'BYP', 'BYPS': 'BYP', 'BYP': 'BYP',
    'CAMP': 'CP', 'CMP': 'CP', 'CP': 'CP',
    'CANYON': 'CYN', 'CANYN': 'CYN', 'CNYN': 'CYN', 'CYN': 'CYN',
    'CAPE': 'CPE', 'CPE': 'CPE',
    'CAUSEWAY': 'CSWY', 'CAUSWA': 'CSWY', 'CSWY': 'CSWY',
    'CENTER': 'CTR', 'CENT': 'CTR', 'CENTR': 'CTR', 'CENTRE': 'CTR', 'CNTER': 'CTR', 'CNTR': 'CTR', 'CTR': 'CTR',
    'CIRCLE': 'CIR', 'CIRCL': 'CIR', 'CIRC': 'CIR', 'CRCL': 'CIR', 'CRCLE': 'CIR', 'CIR': 'CIR',
    'CLIFF': 'CLF', 'CLF': 'CLF',
    'CLUB': 'CLB', 'CLB': 'CLB',
    'COMMON': 'CMN', 'CMN': 'CMN',
    'CORNER': 'COR', 'COR': 'COR',
    'COURSE': 'CRSE', 'CRSE': 'CRSE',
    'COURT': 'CT', 'CRT': 'CT', 'CT': 'CT',
    'COVE': 'CV', 'CV': 'CV',
    'CREEK': 'CRK', 'CRK': 'CRK',
    'CRESCENT': 'CRES', 'CRES': 'CRES',
    'CROSSING': 'XING', 'CRSG': 'XING', 'XING': 'XING',
    'DALE': 'DL', 'DL': 'DL',
    'DRIVE': 'DR', 'DRV': 'DR', 'DR': 'DR',
    'ESTATE': 'EST', 'EST': 'EST',
    'EXPRESSWAY': 'EXPY', 'EXP': 'EXPY', 'EXPW': 'EXPY', 'EXPY': 'EXPY',
    'FIELD': 'FLD', 'FLD': 'FLD',
    'FLAT': 'FLT', 'FLT': 'FLT',
    'FORD': 'FRD', 'FRD': 'FRD',
    'FOREST': 'FRST', 'FRST': 'FRST',
    'FREEWAY': 'FWY', 'FREEWY': 'FWY', 'FRWAY': 'FWY', 'FRWY': 'FWY', 'FWY': 'FWY',
    'GARDEN': 'GDN', 'GARDN': 'GDN', 'GRDEN': 'GDN', 'GRDN': 'GDN', 'GDN': 'GDN',
    'GATEWAY': 'GTWY', 'GATWAY': 'GTWY', 'GTWAY': 'GTWY', 'GTWY': 'GTWY',
    'GLEN': 'GLN', 'GLN': 'GLN',
    'GREEN': 'GRN', 'GRN': 'GRN',
    'GROVE': 'GRV', 'GROV': 'GRV', 'GRV': 'GRV',
    'HARBOR': 'HBR', 'HARBR': 'HBR', 'HBR': 'HBR',
    'HAVEN': 'HVN', 'HVN': 'HVN',
    'HEIGHTS': 'HTS', 'HTS': 'HTS',
    'HIGHWAY': 'HWY', 'HIGHWY': 'HWY', 'HIWAY': 'HWY', 'HIWY': 'HWY', 'HWAY': 'HWY', 'HWY': 'HWY',
    'HILL': 'HL', 'HL': 'HL',
    'HOLLOW': 'HOLW', 'HOLLO': 'HOLW', 'HOLW': 'HOLW',
    'INLET': 'INLT', 'INLT': 'INLT',
    'ISLAND': 'IS', 'ISLND': 'IS', 'IS': 'IS',
    'ISLE': 'ISLE',
    'JUNCTION': 'JCT', 'JCTN': 'JCT', 'JUNCTN': 'JCT', 'JUNCTON': 'JCT', 'JCT': 'JCT',
    'KEY': 'KY', 'KY': 'KY',
    'KNOLL': 'KNL', 'KNL': 'KNL',
    'LAKE': 'LK', 'LK': 'LK',
    'LANDING': 'LNDG', 'LNDNG': 'LNDG', 'LNDG': 'LNDG',
    'LANE': 'LN', 'LN': 'LN',
    'LIGHT': 'LGT', 'LGT': 'LGT',
    'LOAF': 'LF', 'LF': 'LF',
    'LOCK': 'LCK', 'LCK': 'LCK',
    'LODGE': 'LDG', 'LDG': 'LDG',
    'LOOP': 'LOOP',
    'MALL': 'MALL',
    'MANOR': 'MNR', 'MNR': 'MNR',
    'MEADOW': 'MDW', 'MDW': 'MDW',
    'MILL': 'ML', 'ML': 'ML',
    'MISSION': 'MSN', 'MSN': 'MSN',
    'MOUNT': 'MT', 'MT': 'MT',
    'MOUNTAIN': 'MTN', 'MNTAIN': 'MTN', 'MNTN': 'MTN', 'MTIN': 'MTN', 'MTN': 'MTN',
    'NECK': 'NCK', 'NCK': 'NCK',
    'ORCHARD': 'ORCH', 'ORCH': 'ORCH',
    'OVAL': 'OVAL',
    'PARK': 'PARK', 'PRK': 'PARK',
    'PARKWAY': 'PKWY', 'PARKWY': 'PKWY', 'PKWAY': 'PKWY', 'PKY': 'PKWY', 'PKWY': 'PKWY',
    'PASS': 'PASS',
    'PATH': 'PATH',
    'PIKE': 'PIKE',
    'PINE': 'PNE', 'PNE': 'PNE',
    'PLACE': 'PL', 'PL': 'PL',
    'PLAZA': 'PLZ', 'PLZ': 'PLZ',
    'POINT': 'PT', 'PT': 'PT',
    'PORT': 'PRT', 'PRT': 'PRT',
    'PRAIRIE': 'PR', 'PR': 'PR',
    'RADIAL': 'RADL', 'RADL': 'RADL',
    'RANCH': 'RNCH', 'RNCH': 'RNCH',
    'RAPID': 'RPD', 'RPD': 'RPD',
    'REST': 'RST', 'RST': 'RST',
    'RIDGE': 'RDG', 'RDG': 'RDG',
    'RIVER': 'RIV', 'RIV': 'RIV',
    'ROAD': 'RD', 'RD': 'RD',
    'ROW': 'ROW',
    'RUN': 'RUN',
    'SHORE': 'SHR', 'SHR': 'SHR',
    'SPRING': 'SPG', 'SPG': 'SPG',
    'SQUARE': 'SQ', 'SQR': 'SQ', 'SQRE': 'SQ', 'SQU': 'SQ', 'SQ': 'SQ',
    'STREET': 'ST', 'STR': 'ST', 'STRT': 'ST', 'ST': 'ST',
    'TERRACE': 'TER', 'TER': 'TER',
    'TRACE': 'TRCE', 'TRCE': 'TRCE',
    'TRACK': 'TRAK', 'TRK': 'TRAK', 'TRAK': 'TRAK',
    'TRAIL': 'TRL', 'TRL': 'TRL',
    'TUNNEL': 'TUNL', 'TUNNL': 'TUNL', 'TUNL': 'TUNL',
    'TURNPIKE': 'TPKE', 'TRNPK': 'TPKE', 'TPKE': 'TPKE',
    'UNION': 'UN', 'UN': 'UN',
    'VALLEY': 'VLY', 'VALLY': 'VLY', 'VLLY': 'VLY', 'VLY': 'VLY',
    'VIEW': 'VW', 'VW': 'VW',
    'VILLAGE': 'VLG', 'VILLAG': 'VLG', 'VILLG': 'VLG', 'VLG': 'VLG',
    'VILLE': 'VL', 'VL': 'VL',
    'VISTA': 'VIS', 'VIST': 'VIS', 'VIS': 'VIS',
    'WALK': 'WALK',
    'WAY': 'WAY',
    'WELL': 'WL', 'WL': 'WL',
    'WOOD': 'WD', 'WD': 'WD',
    'WOODS': 'WDS', 'WDS': 'WDS'
  };

  const handleRevalidateAllCombinations = async () => {
    const isInitial =!baseAddressParts.streetNumber && !baseAddressParts.streetName && !baseAddressParts.city && !baseAddressParts.state && !baseAddressParts.zipcode;
    if (isInitial) {
      console.warn(
        "Revalidation skipped: baseAddressParts not yet populated. Try again after reverse geocode."
      );
      return; // Do nothing yet
    }
    if (
      !baseAddressParts.streetNumber || !baseAddressParts.streetName || !baseAddressParts.city || !baseAddressParts.state || !baseAddressParts.zipcode) {
      alert(
        "Missing some base address parts. Please run address validation again."
      );
      return;
    }

    // console.log("Base Address Parts ready:", baseAddressParts);

    const { reverseStreets = [] } = reverseResult || {};
    // const { streetNumber, city, state , zipcode} = baseAddressParts;
    //   if (!streetNumber || !city || !state || !zipcode) {
    //     console.log(streetNumber,city,state,zipcode)
    //     alert("Missing street number, city, state, or zipcode from the original address.");
    //     return;
    //   }
      const combinations = [];
        for (const reverseStreet of reverseStreets) {
             const fullAddress = `${baseAddressParts.streetNumber} ${reverseStreet}, ${baseAddressParts.city}, ${baseAddressParts.state} ${baseAddressParts.zipcode}`;
             combinations.push(fullAddress);
          // }
        }
        const results = [];
        let foundAABB = false;
        
        for (const combo of combinations) {
          setLoading(true);
          try {
            const res = await fetch("/api/google-address-validation", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ address: combo }),
            });
            const json = await res.json();
            const footnote = json.result?.result?.uspsData?.dpvFootnote || "No DPV found";
            // console.log(combo,footnote)
            results.push({ address: combo, dpvFootnote: footnote });
            if (footnote === "AABB") {
              foundAABB = true;
              handleForwardGeocoding(combo);
              break; // exit loop immediately
            }
          } catch (error) {
            results.push({ address: combo, dpvFootnote: "Error during validation" });
          }
        }
        if (!foundAABB) {
          handleForwardGeocoding(address);
        }
        // setLoading(false);

      // setBatchValidationResults(results);
      //console.log('batchValidationResults:', batchValidationResults);
    // }
  };
  
  const handleForwardGeocoding = async (finalAddress) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({ address: finalAddress });
  
      const endpoints = localState ?
        [{ name: "Google", url: `http://localhost:3001/api/forward-google?${queryParams}` },
        { name: "TomTom", url: `http://localhost:3001/api/forward-tomtom?${queryParams}` },
        { name: "Here", url: `http://localhost:3001/api/forward-here?${queryParams}` },
        { name: "OSM", url: `http://localhost:3001/api/forward-osm?${queryParams}` },
        { name: "Azure", url: `http://localhost:3001/api/forward-azure?${queryParams}` },
        { name: "USCensus", url: `http://localhost:3001/api/forward-census?${queryParams}` },] :
        [{ name: "Google", url: `/api/forward-google?${queryParams}` },
        { name: "TomTom", url: `/api/forward-tomtom?${queryParams}` },
        { name: "Here", url: `/api/forward-here?${queryParams}` },
        { name: "OSM", url: `/api/forward-osm?${queryParams}` },
        { name: "Azure", url: `/api/forward-azure?${queryParams}` }, 
        { name: "USCensus", url: `/api/forward-census?${queryParams}` },];
  
      const coordinateResults = [];
  
      // ✅ Validate and push input lat/lng
      const inputLat = parseFloat(lat);
      const inputLng = parseFloat(lng);
      if (!isNaN(inputLat) && !isNaN(inputLng)) {
        const inputCoord = {
          lat: inputLat,
          lng: inputLng,
          source: "Input"
        };
        // console.log("Input Lat/Lng:", inputCoord);
        coordinateResults.push(inputCoord);
      } else {
        // console.warn("Invalid input lat/lng; skipping input coordinates.");
      }
  
      for (const endpoint of endpoints) {
        try {
          const res = await fetch(endpoint.url);
          const data = await res.json();
          let latLng = null;

          // console.log(res)
          // console.log(data)
  
          switch (endpoint.name) {
            case "Google":
              latLng = data?.results?.[0]?.geometry?.location;
              break;
            case "TomTom":
              latLng = {
                lat: data?.results?.[0]?.position?.lat,
                lng: data?.results?.[0]?.position?.lon,
              };
              break;
            case "Here":
              latLng = {
                lat: data?.items?.[0]?.position?.lat,
                lng: data?.items?.[0]?.position?.lng,
              };
              break;
            case "OSM":
              // console.log(`${endpoint.name} Raw Response:`, data);
              latLng = {
                lat: Number(data?.[0].lat),
                lng: Number(data?.[0].lon),
              };
              break;
            case "USCensus":
              latLng = {
                lat: parseFloat(data?.result?.addressMatches?.[0]?.coordinates?.y),
                lng: parseFloat(data?.result?.addressMatches?.[0]?.coordinates?.x),
              };
              break;
            case "Azure":
              latLng = {
                lat: data?.results?.[0]?.position?.lat,
                lng: data?.results?.[0]?.position?.lon,
              };
              break;
          }
  
          if (latLng?.lat && latLng?.lng && !isNaN(latLng.lat) && !isNaN(latLng.lng)) {
            const resultWithSource = {
              lat: latLng.lat,
              lng: latLng.lng,
              source: endpoint.name // Capitalized and consistent
            };
            // console.log(`Source: ${resultWithSource.source} | Lat: ${resultWithSource.lat}, Lng: ${resultWithSource.lng}`);
            coordinateResults.push(resultWithSource);
          }
        } catch (err) {
          console.warn(`Failed to fetch or parse ${endpoint.name} response:`, err);
        } finally {
          //  setLoading(false);
        }
      }
      
      setGeocodedCoordinates(coordinateResults);
      setSelectedCoordinateIndex(0);
      // console.log("Coordinate Results:", geocodedCoordinates);

      setReverseResult({
        step: "Forward Geocoding",
        forwardResults: coordinateResults,
      });
    } catch (error) {
      console.error("Error during forward geocoding step 2:", error);
    } finally {
      // setLoading(false);
    }
  };

  return (
    
    <div className="container">

      {loading && (
        <div style={{position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000}}>
          <div style={{background: '#fff', padding: '20px 40px', borderRadius: '8px', fontSize: '1.5rem', fontWeight: 'bold'}}>
            Loading...
          </div>
        </div>
      )}
      <div class='heading1'>
          <h1>GIS Address Validation</h1>
      </div>
      <div className="card">
        <div class='section1'>
          <h2 className="heading2">Single Address Lookup Tool</h2>
        </div>
        <div class='section1a'>
          <input className="input-row" type="text" value={slid} size={10} onChange={(e) => setSlid(e.target.value)} onPaste={handleSmartPaste} placeholder="SLID"/>
          <input className="input-row" type="text" value={address} size={60} onChange={(e) => setAddress(e.target.value)} onPaste={handleSmartPaste} placeholder="Full Address"/>
          <input className="input-row" type="text" value={lat} size={15} onChange={(e) => setLat(e.target.value)} onPaste={handleSmartPaste} placeholder="Latitude"/>
          <input className="input-row" type="text" value={lng} size={15} onChange={(e) => setLng(e.target.value)} onPaste={handleSmartPaste} placeholder="Longitude"/>
          <button className='input-button' onClick={handleAddressValidation}>Start Address Lookup</button>
          <div class="cassSwitch">
            <label class="toggle-switch">
              <input type="checkbox" id="myToggle" checked={cassState} onChange={(e) => setCassState(e.target.checked)}/>
              <span class="slider round"></span>
            </label>
            <div style={{alignSelf: 'center', marginLeft: '10px',marginTop: '10px'}}>Enable CASS</div>
          </div>
        </div>
          <h2 className="heading2">- OR -</h2>
        <div className="section1b">
          <button className='demo-button' onClick={runNextTestCase}>Demo Sample Address</button>
        </div>
      </div>

      {validationResult && (
        <div className="card">
          <p><strong>Formatted Address:</strong> {validationResult.formattedAddress}</p>
          <p><strong>DPV Footnote:</strong> {validationResult.dpvFootnote}</p>
        </div>
      )}

      {selectedCoordinate && (
        <div className="card">
          <div className="map-container">
            <GoogleMapWithStreetView coordinates={geocodedCoordinates} baseAddressParts={baseAddressParts} county = {county}/>
          </div>
        </div>
      )}
      
    </div>
    
  );
}

export default App;